import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePlatformAccessLevel } from '../features/platformPermissions/usePlatformAccessLevel';
import { ChangeRequestFormModal } from '../features/change-management/ChangeRequestFormModal';
import { VerifyChangeModal } from '../features/change-management/VerifyChangeModal';
import {
  createChangeRequest,
  escalateChangeRequest,
  fetchChangeRequests,
  transitionChangeRequestStatus,
  updateChangeRequest,
} from '../features/change-management/changeRequestsApi';
import type { ChangeRequestSummary } from '../features/change-management/types';
import {
  CHANGE_TYPE_LABEL,
  NEXT_STATUS,
  NEXT_STATUS_ACTION,
  STANDARD_LABEL,
  STATUS_LABEL,
  statusBadgeClass,
  typeBadgeClass,
} from '../features/change-management/changeUtils';

export function ChangeManagementPage() {
  const { canEdit } = usePlatformAccessLevel('change_management.access');
  const [items, setItems] = useState<ChangeRequestSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<ChangeRequestSummary | null>(null);
  const [verifyItem, setVerifyItem] = useState<ChangeRequestSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await fetchChangeRequests({
        page,
        pageSize: 20,
        status: filterStatus || undefined,
        search: search.trim() || undefined,
        sortBy: 'createdAt',
        sortDir: 'desc',
      });
      setItems(list.data);
      setTotalPages(list.pagination.pages);
    } catch {
      setError('No se pudieron cargar las solicitudes de cambio.');
    } finally {
      setLoading(false);
    }
  }, [page, filterStatus, search]);

  useEffect(() => {
    void load();
  }, [load]);

  const advanceStatus = async (item: ChangeRequestSummary) => {
    const next = NEXT_STATUS[item.status];
    if (!next) return;
    if (next === 'cerrado') {
      setVerifyItem(item);
      return;
    }
    await transitionChangeRequestStatus(item.id, {
      status: next,
      ...(next === 'en_evaluacion' && item.riskAssessmentNote
        ? { riskAssessmentNote: item.riskAssessmentNote }
        : {}),
    });
    await load();
  };

  const rejectChange = async (item: ChangeRequestSummary) => {
    await transitionChangeRequestStatus(item.id, { status: 'rechazado' });
    await load();
  };

  const escalate = async (item: ChangeRequestSummary) => {
    await escalateChangeRequest(item.id);
    await load();
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Gestión del cambio</h1>
          <p className="text-muted-foreground text-sm">
            Cambios planificados del sistema de gestión (cl. 6.3 / 8.1)
          </p>
        </div>
        {canEdit && (
          <button type="button" className="btn btn-primary" onClick={() => setCreateOpen(true)}>
            Nueva solicitud
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <input
          type="search"
          placeholder="Buscar por código o título…"
          className="rounded-md border border-border px-3 py-2 text-sm min-w-[200px]"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="rounded-md border border-border px-3 py-2 text-sm"
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          {Object.entries(STATUS_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {error && <p className="text-destructive text-sm">{error}</p>}

      <div className="overflow-x-auto border border-border rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-3 py-2 text-left">Código</th>
              <th className="px-3 py-2 text-left">Título</th>
              <th className="px-3 py-2 text-left">Tipo</th>
              <th className="px-3 py-2 text-left">Normas</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-left">Riesgos</th>
              <th className="px-3 py-2 text-left">Planificado</th>
              {canEdit && <th className="px-3 py-2 text-left">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={canEdit ? 8 : 7} className="px-3 py-6 text-center text-muted-foreground">
                  Cargando…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={canEdit ? 8 : 7} className="px-3 py-6 text-center text-muted-foreground">
                  Sin solicitudes de cambio.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-t border-border">
                  <td className="px-3 py-2 font-mono">{item.code}</td>
                  <td className="px-3 py-2">
                    <div>{item.title}</div>
                    {item.changeType === 'documento' && (
                      <Link to="/documentos" className="text-primary text-xs underline">
                        Ver documentos
                      </Link>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex rounded px-2 py-0.5 text-xs ${typeBadgeClass(item.changeType)}`}
                    >
                      {CHANGE_TYPE_LABEL[item.changeType]}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {item.affectedStandards.map((s) => (
                        <span key={s} className="text-xs bg-muted rounded px-1.5 py-0.5">
                          {STANDARD_LABEL[s]}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-flex rounded px-2 py-0.5 text-xs ${statusBadgeClass(item.status)}`}
                    >
                      {STATUS_LABEL[item.status]}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {(item.linkedRisks ?? []).length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <div className="flex flex-col gap-0.5">
                        {item.linkedRisks!.map((risk) => (
                          <Link
                            key={risk.id}
                            to={`/riesgos/${risk.id}`}
                            className="text-primary text-xs underline"
                          >
                            {risk.code}
                          </Link>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {item.plannedDate
                      ? new Date(item.plannedDate).toLocaleDateString()
                      : '—'}
                  </td>
                  {canEdit && (
                    <td className="px-3 py-2 space-x-1">
                      {item.status !== 'rechazado' && item.status !== 'cerrado' && (
                        <button
                          type="button"
                          className="btn btn-outline btn-xs"
                          onClick={() => setEditItem(item)}
                        >
                          Editar
                        </button>
                      )}
                      {NEXT_STATUS[item.status] && (
                        <button
                          type="button"
                          className="btn btn-outline btn-xs"
                          onClick={() => void advanceStatus(item)}
                        >
                          {NEXT_STATUS_ACTION[item.status]}
                        </button>
                      )}
                      {item.status === 'en_evaluacion' && (
                        <button
                          type="button"
                          className="btn btn-outline btn-xs"
                          onClick={() => void rejectChange(item)}
                        >
                          Rechazar
                        </button>
                      )}
                      {!item.bridgeRecordId &&
                        item.status !== 'cerrado' &&
                        item.status !== 'rechazado' && (
                          <button
                            type="button"
                            className="btn btn-outline btn-xs"
                            onClick={() => void escalate(item)}
                          >
                            Escalar NC
                          </button>
                        )}
                      {item.bridgeRecordId && (
                        <Link
                          className="text-primary text-xs underline"
                          to={`/registros/no-conformidades/${item.bridgeRecordId}`}
                        >
                          Ver NC
                        </Link>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Anterior
          </button>
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Siguiente
          </button>
        </div>
      )}

      <ChangeRequestFormModal
        open={createOpen}
        title="Nueva solicitud de cambio"
        onClose={() => setCreateOpen(false)}
        onSubmit={async (payload) => {
          await createChangeRequest(payload);
          await load();
        }}
      />

      <ChangeRequestFormModal
        open={editItem != null}
        title={`Editar ${editItem?.code ?? ''}`}
        showRiskAssessment={editItem?.status === 'en_evaluacion'}
        initial={
          editItem
            ? {
                title: editItem.title,
                description: editItem.description,
                changeType: editItem.changeType,
                affectedStandards: editItem.affectedStandards,
                reason: editItem.reason,
                riskAssessmentNote: editItem.riskAssessmentNote,
                linkedRiskIds: editItem.linkedRisks?.map((r) => r.id),
                plannedDate: editItem.plannedDate,
              }
            : undefined
        }
        onClose={() => setEditItem(null)}
        onSubmit={async (payload) => {
          if (!editItem) return;
          await updateChangeRequest(editItem.id, payload);
          await load();
        }}
      />

      <VerifyChangeModal
        open={verifyItem != null}
        onClose={() => setVerifyItem(null)}
        onConfirm={async (verificationNote) => {
          if (!verifyItem) return;
          await transitionChangeRequestStatus(verifyItem.id, {
            status: 'cerrado',
            verificationNote,
          });
          await load();
        }}
      />
    </div>
  );
}
